"use client";
import { jsx } from "react/jsx-runtime";
import { useId, useMemo } from "react";
import { createContextScope } from "@radix-ui/react-context";
const PROGRESS_CIRCLE_PROVIDER_NAME = "ProgressCircleProvider";
const [createProgressCircleContext, createProgressCircleScope] = createContextScope(PROGRESS_CIRCLE_PROVIDER_NAME);
const [ProgressCircleContextProvider, useProgressCircleContext] = createProgressCircleContext(PROGRESS_CIRCLE_PROVIDER_NAME);
function ProgressCircleProvider({ __scopeProgressCircle, children, formatValue, id: propertyId, max = 100, min = 0, size = 48, startAngle = -90, strokeWidth = 4, thresholds, value }) {
    const uniqueId = useId();
    const id = propertyId ?? uniqueId;
    const validSize = Math.max(0, size);
    const validStrokeWidth = Math.max(0, strokeWidth);
    const validStartAngle = startAngle % 360;
    let validMin = min;
    let validMax = max;
    if (validMin > validMax) [validMin, validMax] = [
        validMax,
        validMin
    ];
    const isIndeterminate = null == value;
    const clampedValue = isIndeterminate ? void 0 : clamp(validMin, validMax, value);
    const range = validMax - validMin;
    const percentage = void 0 !== clampedValue && range > 0 ? (clampedValue - validMin) / range * 100 : 0;
    const valueText = void 0 !== clampedValue && formatValue ? formatValue(clampedValue) : `${String(Math.round(percentage))}%`;
    const sortedThresholds = useMemo(()=>thresholds && thresholds.length > 0 ? [
            ...thresholds
        ].sort((a, b)=>a.value - b.value) : [], [
        thresholds
    ]);
    const threshold = useMemo(()=>{
        if (void 0 === clampedValue) return;
        for (const sortedThreshold of sortedThresholds)if (clampedValue <= sortedThreshold.value) return sortedThreshold;
        return sortedThresholds.at(-1);
    }, [
        sortedThresholds,
        clampedValue
    ]);
    const center = validSize / 2;
    const radius = Math.max(0, center - validStrokeWidth / 2);
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - percentage / 100 * circumference;
    const rotationTransform = `rotate(${String(validStartAngle)}, 0, 0)`;
    return /*#__PURE__*/ jsx(ProgressCircleContextProvider, {
        center: center,
        circumference: circumference,
        clampedValue: clampedValue,
        id: id,
        max: validMax,
        min: validMin,
        radius: radius,
        rotationTransform: rotationTransform,
        scope: __scopeProgressCircle,
        size: validSize,
        strokeDashoffset: strokeDashoffset,
        strokeWidth: validStrokeWidth,
        threshold: threshold,
        value: value ?? 0,
        valueText: valueText,
        children: children
    });
}
function ProgressCircle({ __scopeProgressCircle, ...props }) {
    return /*#__PURE__*/ jsx("div", {
        ...props
    });
}
const PROGRESS_CIRCLE_SVG_NAME = "ProgressCircleSVG";
function ProgressCircleSVG({ __scopeProgressCircle, ...props }) {
    const { clampedValue, id, max, min, size, valueText } = useProgressCircleContext(PROGRESS_CIRCLE_SVG_NAME, __scopeProgressCircle);
    return /*#__PURE__*/ jsx("svg", {
        "aria-label": "Progress",
        "aria-valuemax": max,
        "aria-valuemin": min,
        "aria-valuenow": clampedValue,
        "aria-valuetext": void 0 === clampedValue ? void 0 : valueText,
        height: size,
        id: id,
        role: "progressbar",
        viewBox: `0 0 ${String(size)} ${String(size)}`,
        width: size,
        ...props
    });
}
const PROGRESS_CIRCLE_TRACK_NAME = "ProgressCircleTrack";
function ProgressCircleTrack({ __scopeProgressCircle, ...props }) {
    const { center, radius, strokeWidth, threshold } = useProgressCircleContext(PROGRESS_CIRCLE_TRACK_NAME, __scopeProgressCircle);
    return /*#__PURE__*/ jsx("circle", {
        cx: center,
        cy: center,
        fill: "transparent",
        r: radius,
        stroke: threshold?.background ?? "currentColor",
        strokeWidth: strokeWidth,
        ...props
    });
}
const PROGRESS_CIRCLE_INDICATOR_NAME = "ProgressCircleIndicator";
function ProgressCircleIndicator({ __scopeProgressCircle, ...props }) {
    const { center, circumference, radius, rotationTransform, strokeDashoffset, strokeWidth, threshold } = useProgressCircleContext(PROGRESS_CIRCLE_INDICATOR_NAME, __scopeProgressCircle);
    return /*#__PURE__*/ jsx("circle", {
        cx: center,
        cy: center,
        fill: "transparent",
        r: radius,
        stroke: threshold?.color ?? "currentColor",
        strokeDasharray: circumference,
        strokeDashoffset: strokeDashoffset,
        strokeLinecap: "round",
        strokeWidth: strokeWidth,
        transform: rotationTransform,
        ...props
    });
}
const PROGRESS_CIRCLE_VALUE_NAME = "ProgressCircleValue";
function ProgressCircleValue({ __scopeProgressCircle, children, ...props }) {
    const { clampedValue, valueText } = useProgressCircleContext(PROGRESS_CIRCLE_VALUE_NAME, __scopeProgressCircle);
    if ("function" == typeof children) return children({
        value: clampedValue,
        valueText
    });
    return /*#__PURE__*/ jsx("div", {
        ...props,
        children: children ?? valueText
    });
}
function clamp(min, max, value) {
    return Math.min(max, Math.max(min, value));
}
export { ProgressCircleIndicator as Indicator, ProgressCircle, ProgressCircleIndicator, ProgressCircleProvider, ProgressCircleSVG, ProgressCircleTrack, ProgressCircleValue, ProgressCircleProvider as Provider, ProgressCircle as Root, ProgressCircleSVG as SVG, ProgressCircleTrack as Track, ProgressCircleValue as Value, createProgressCircleScope };
